import { graphHooks } from '@tunasong/graph-lib/react'
import { fillCopy, requestAccessCopy as copy, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { AccessRequestRow } from './access-request-row.js'
import { useAccessRequestActions } from './use-access-request-actions.js'
import { useRefreshOnAccessRequestNotification } from './use-refresh-on-access-request-notification.js'
import { useRowSettleFocus } from './use-row-settle-focus.js'
import styles from './access-request-list.module.css'

export interface AccessRequestListProps {
  entityId: string
  /** WRITE holders only (request-access spec §6.3, decision P1) */
  canShare: boolean
  /**
   * Where focus goes once the last request is handled, in order; the first that exists wins (§8.4). The member
   * heading is not rendered for an item with no collaborators, so a later fallback keeps focus off `<body>`.
   */
  focusFallbackIds: string[]
}

/** Share dialog → Users tab, above the member list (spec §8.4). Hidden when there are none. */
export const AccessRequestList: FC<AccessRequestListProps> = props => {
  const { entityId, canShare, focusFallbackIds } = props
  useRefreshOnAccessRequestNotification()
  const { data: requests } = graphHooks.useEntityAccessRequests(entityId, { skip: !canShare })
  const actions = useAccessRequestActions()
  const count = requests?.length ?? 0
  const { listRef, settle } = useRowSettleFocus({ count, fallbackIds: focusFallbackIds })

  return (
    <>
      {canShare && requests && count > 0 ? (
        <section className={styles.section} aria-labelledby="share-access-requests">
          <Typography id="share-access-requests" variant="subtitle1" component="h3" className={styles.heading}>
            {fillCopy(copy.shareHeading, { n: count })}
          </Typography>
          <ul ref={listRef} className={styles.list}>
            {requests.map((view, index) => (
              <AccessRequestRow
                key={view.requester.userId}
                view={view}
                layout="dialog"
                actions={actions}
                onSettled={() => settle(index)}
              />
            ))}
          </ul>
        </section>
      ) : null}
      {/* Outside the section, so Undo stays visible after the last row has left */}
      {actions.toast}
    </>
  )
}
